/**
 * 轻量 SEO 工具
 * 项目是 Vue2 SPA 且未引入 vue-meta，这里手动维护 <head> 里的 TDK / OG / canonical。
 * 阶段说明：hash 路由下内页仍需阶段 2（预渲染 / 快照）才能被完整收录，
 * 本模块解决的是「页面有独立且贴合内容的 TDK」这一层。
 */

const SITE_NAME = '铭顺转铺网'
const SITE_URL = 'https://www.mszpw.com.cn'

// 当前路由名，用于防止 A 页面接口慢返回时把 TDK 覆盖到已跳转的 B 页面
let currentRouteName = ''

export function setCurrentRoute(name) {
    currentRouteName = name || ''
}

/** 新增或更新一个 meta 标签；content 为空时移除该标签 */
function upsertMeta(attr, key, content) {
    const selector = 'meta[' + attr + '="' + key + '"]'
    let el = document.head.querySelector(selector)
    if (!content) {
        if (el) el.parentNode.removeChild(el)
        return
    }
    if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
    }
    el.setAttribute('content', content)
}

function setCanonical(href) {
    let el = document.head.querySelector('link[rel="canonical"]')
    if (!href) {
        if (el) el.parentNode.removeChild(el)
        return
    }
    if (!el) {
        el = document.createElement('link')
        el.setAttribute('rel', 'canonical')
        document.head.appendChild(el)
    }
    el.setAttribute('href', href)
}

/** 当前页面 URL（hash 路由下含 # 部分，用于 og:url） */
function currentUrl() {
    try {
        return window.location.href.split('#')[0] + (window.location.hash || '')
    } catch (e) {
        return SITE_URL
    }
}

/**
 * 应用页面 SEO 信息
 * @param {Object} meta
 * @param {string} meta.title        页面标题
 * @param {string} meta.description  页面描述
 * @param {string} meta.keywords     页面关键词
 * @param {boolean} meta.noindex     是否禁止收录（会员中心/登录等）
 * @param {string} meta.image        分享缩略图
 * @param {string} meta.canonical    规范链接；hash 阶段默认不设置
 * @param {string} meta.routeName    限定生效的路由名（详情页异步回调用）
 */
export function applySeo(meta) {
    const m = meta || {}
    if (m.routeName && currentRouteName && m.routeName !== currentRouteName) return

    if (m.title) document.title = m.title

    upsertMeta('name', 'description', m.description)
    upsertMeta('name', 'keywords', m.keywords)
    upsertMeta('name', 'robots', m.noindex ? 'noindex,nofollow' : '')

    upsertMeta('property', 'og:site_name', SITE_NAME)
    upsertMeta('property', 'og:type', m.type || 'website')
    upsertMeta('property', 'og:title', m.title)
    upsertMeta('property', 'og:description', m.description)
    upsertMeta('property', 'og:image', m.image)
    upsertMeta('property', 'og:url', currentUrl())

    // 只有显式传了 canonical 才改，避免详情页动态 TDK 把路由级 canonical 误删
    if (Object.prototype.hasOwnProperty.call(m, 'canonical')) {
        setCanonical(m.canonical)
    }
}

/**
 * 由房源详情数据拼装详情页 TDK
 * 用真实字段（区域/面积/租金/行业）生成，避免千页一面
 * @param {Object} info        接口返回的详情对象
 * @param {Object} cfg
 * @param {string} cfg.pageLabel   页面类型词，如「商铺转让」
 * @param {string} cfg.routeName   当前路由名，防止异步覆盖
 */
export function buildDetailSeo(info, cfg) {
    const d = info || {}
    const config = cfg || {}
    const label = config.pageLabel || '商铺信息'
    const city = d.city_name || ''
    const district = d.district_name || d.business_area || ''
    const region = (city + district).replace(/市$/, '') || ''
    const title = d.title || label

    const img = d.cover_image_url
        || (d.image_list && d.image_list[0])
        || (d.cover_image_list && d.cover_image_list[0])
        || ''

    const descParts = ['铭顺转铺网提供' + (region ? region : '') + label + '信息：' + title]
    const facts = []
    if (d.building_area) facts.push('建筑面积' + d.building_area + '㎡')
    if (d.rent_text) facts.push('租金' + d.rent_text)
    if (d.transfer_fee) facts.push('转让费' + d.transfer_fee + '元')
    if (d.business_type) facts.push('行业' + d.business_type)
    if (d.industry_text) facts.push(d.industry_text)
    if (facts.length) descParts.push(facts.join('，'))
    descParts.push('找商铺、转店铺、租旺铺，就上' + SITE_NAME)

    const words = []
    if (region) words.push(region + label)
    if (city) words.push(city + label)
    if (district) words.push(district + label)
    words.push(label, title, SITE_NAME)

    return {
        title: title + '_' + (region ? region : '') + label + ' - ' + SITE_NAME,
        description: descParts.join('。').slice(0, 160),
        keywords: words.filter(Boolean).join(','),
        image: img,
        type: 'article',
        routeName: config.routeName
    }
}
